import { useCallback, useEffect, useRef } from 'react';

/** Publishes an element's height as a CSS variable on the page (`--nav-height: 73px`), kept current as
 * it changes: large text, a browser's own bars and safe areas make bottom bars taller than any number
 * written in CSS. Gone (0) when the element is not shown. Returns a ref callback for the element. */
export function useHeightVar(name: `--${string}`) {
  const observer = useRef<ResizeObserver | null>(null);

  const ref = useCallback(
    (el: HTMLElement | null) => {
      observer.current?.disconnect();
      observer.current = null;
      const root = document.documentElement;
      if (!el) {
        root.style.removeProperty(name);
        return;
      }
      const set = () => root.style.setProperty(name, `${el.offsetHeight}px`);
      set();
      if (typeof ResizeObserver === 'function') {
        observer.current = new ResizeObserver(set);
        observer.current.observe(el);
      }
    },
    [name],
  );

  useEffect(() => () => observer.current?.disconnect(), []);
  return ref;
}
